import { capitalize, capitalizeFirstWord } from "@/shared";

import type { CatalogMetaResponse } from "./catalog.type";
import {
  getCurrentBreadcrumbName,
  getRouteSlug,
  parseCatalogRoute,
} from "./catalogRoute.helper";
import type { Facet } from "./filters.type";

type CatalogRouteType = "search" | "brand" | "category";

type CatalogTitleInfo = {
  type: CatalogRouteType;
  title: string;
};

function decodeSlug(slug: string): string {
  try {
    return decodeURIComponent(slug).replace(/-/g, " ");
  } catch {
    return slug.replace(/-/g, " ");
  }
}

/** Label do valor selecionado no facet de marca (field "brand"). */
function getSelectedBrandLabel(facets: Facet[]): string | undefined {
  const brand = facets.find((facet) => facet.field === "brand");

  if (!brand || !("values" in brand)) return undefined;

  return (
    brand.values.find((value) => value.is_selected)?.label?.trim() || undefined
  );
}

/**
 * Label da categoria atual no facet de categoria (field "categories_ids").
 * Só há categoria aplicada a partir do nível 1 — o nível 0 é a raiz ("Categoria").
 */
function getSelectedCategoryLabel(facets: Facet[]): string | undefined {
  const category = facets.find((facet) => facet.field === "categories_ids");

  if (category?.component !== "CustomCategory" || category.tree.level < 1) {
    return undefined;
  }

  return category.tree.label?.trim() || undefined;
}

/**
 * Título das rotas de marca (/b): única fonte da verdade é a composição
 * "Categoria Marca" a partir dos facets aplicados. A marca é sempre esperada;
 * a categoria entra apenas quando aplicável (omitida se não houver). Sem
 * fallback para breadcrumb/slug — o breadcrumb de marca não segue o padrão de
 * categorias e traz apenas a própria marca.
 *
 * Exportada para ser reusada pelo `<title>` (SEO) em `getCatalogMetadata`,
 * mantendo H1 e título da aba com a mesma composição e fonte (facets).
 */
export function buildBrandTitle(facets: Facet[]): string {
  const brand = getSelectedBrandLabel(facets);
  const category = getSelectedCategoryLabel(facets);

  return [category, brand].filter(Boolean).join(" ");
}

/**
 * Título das rotas de categoria (/c): nome do breadcrumb (último crumb
 * não-home), que preserva acentos/caracteres especiais. Sem breadcrumb, cai no
 * slug da URL capitalizado (normalizado, perde acentos).
 */
export function buildCategoryTitle(
  meta: CatalogMetaResponse | undefined,
  slug: string,
): string {
  return getCurrentBreadcrumbName(meta) ?? capitalize(decodeSlug(slug));
}

/**
 * Título das rotas de busca (/s): termo pesquisado derivado do slug, com apenas
 * a primeira palavra capitalizada.
 */
export function buildSearchTitle(slug: string): string {
  return capitalizeFirstWord(decodeSlug(slug));
}

export function getCatalogTitle(
  pathname: string,
  meta?: CatalogMetaResponse,
  facets: Facet[] = [],
): CatalogTitleInfo {
  const slug = getRouteSlug(pathname);

  if (!slug) {
    return { type: "search", title: "" };
  }

  const { kind } = parseCatalogRoute(pathname);

  switch (kind) {
    case "brand":
      return {
        type: "brand",
        title: buildBrandTitle(facets),
      };
    case "category":
      return {
        type: "category",
        title: buildCategoryTitle(meta, slug),
      };
    default:
      return {
        type: "search",
        title: buildSearchTitle(slug),
      };
  }
}
